import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { GridPageLayout } from '@lux/grid';
import { Tag } from '@lux/title';
import { useIsEligibleForVoucher } from '@pegasus/platform-journeys';
import {
    LoyaltyOnboardingScreen,
    LoyaltyStatus,
    PlusContentDialog,
    PlusContentDialogType,
    useLoyaltyOnboardingScreen,
    usePlusContentDialog,
} from '@pegasus/plus';
import { TranslateScope, useTranslate } from '@pegasus/translation';
import { BottomBubble, ErrorBoundary } from '@browsing/components';
import { useConfig } from '@browsing/config';
import { useQuery, usePlusExclusive } from '@browsing/hooks';

import { MYLOUNGE_ROUTE } from '@client/config/constants';
import { CSRF_HEADER, XSRF } from '@shared/constants';
import { RootState } from '@client/redux/types';
import { getIsMobileScreen } from '@client/redux/modules/clientDevice/selectors';
import {
    getCustomer,
    getHasEmailConfirmed,
    State as CustomerState,
    getGender,
    fetchCustomer,
} from '@client/redux/modules/customer';
import {
    getCategoryTabId,
    getOpenCampaigns,
    getTabs,
    makeGetUpcomingCampaigns,
} from '@client/redux/modules/mylounge';

import { incrementPageVisitsCount } from '@client/helpers';

import { getTrackingData } from '@client/redux/modules/tracking/selectors';
import {
    trackSelectContent,
    trackViewContent,
    useTrackCampaignsImpression,
} from '@browsing/tracking';

import { HighlightsCarousel } from '@browsing/highlights';
import { useLocation, useNavigate } from 'react-router-dom';

import { sleep } from '@pegasus/utils';
import { getCookie, ONE_DAY, ONE_HOUR, setCookie } from '@client/helpers/cookie';
import { RestClient } from '@pegasus/rest-client';
import { getCSRFCookie } from '@browsing/utils';
import {
    fetchFlashMessages,
    showHeaderFlashMessage,
} from '@client/redux/modules/flashMessage/actions';
import { getFlashMessages } from '@client/redux/modules/flashMessage/selectors';
import { AccessibleHeader } from '../shared/accessible-header';
import { HeroBanner } from './banner/banner';
import { Grid } from './campaigns/grid/Grid';
import UpcomingSection from './campaigns/upcoming/UpcomingSectionWrapper';

import {
    CAMPAIGN_WRAPPER,
    CONTINUE_SHOPPING_HASH,
    EXPLORE_YOUR_REWARDS_HASH,
    FREE_DELIVERY_COOKIE,
    FREE_DELIVERY_SESSION_COOKIE,
    GRANTED,
    HIGHLIGHTS_CAROUSEL_WRAPPER,
    LATER_HASH,
    LOYALTY_OPT_IN_ONBOARDING_DIALOG_COOKIE,
    PLUS_HASH,
    TERMS_AND_CONDITIONS_HASH,
} from './constants';
import { CampaignExpiredStatus, ExpiredCampaignDialog } from './expired-campaign-dialog';
import { CrossCampaignEntrySmallScreen, useCrossCampaignEntry } from './cross-campaign';
import { FreeDeliveryBenefitsDialog, useZalandoPlus } from './plus';

import * as S from './mylounge-wrapper.styles';
import { useLoyalty } from './hooks/use-loyalty';
import { useIsPlatformJourney } from './platform-journeys';
import { GRECarouselComponent, useGRE } from './gre';
import { FlashMessage } from './flash-messages/FlashMessage';

const MyLoungeWrapper: React.FC = () => {
    const query = useQuery();
    const plus = useZalandoPlus();
    const isMobileScreen = useSelector(getIsMobileScreen);
    const tabs = useSelector(getTabs);
    const openCampaigns = useSelector(getOpenCampaigns);
    const isMailConfirmed = useSelector(getHasEmailConfirmed);
    const flashMessages = useSelector(getFlashMessages);
    const location = useLocation();
    const isPlusExclusiveEnabled = usePlusExclusive();
    const isPlatformJourneyEnabled = useIsPlatformJourney();
    const isEligibleForVoucher = useIsEligibleForVoucher();
    const { showCrossCampaignFilterInMobile } = useCrossCampaignEntry();
    const translate = useTranslate('myLounge');

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const customer = useSelector(getCustomer);

    const tracking = useSelector(getTrackingData);
    const categoryTabId = useSelector(getCategoryTabId);

    useTrackCampaignsImpression({
        categoryTabId,
        isMobileScreen,
        visitorID: tracking?.visitorID,
    });

    useEffect(() => {
        incrementPageVisitsCount('zl_mylounge_page_view_count');
    }, []);

    useEffect(() => {
        dispatch(fetchFlashMessages());
    }, [dispatch]);

    const getUpcomingCampaigns = useMemo(makeGetUpcomingCampaigns, []);
    const upcomingCampaigns = useSelector((state: RootState) =>
        getUpcomingCampaigns(state, {
            isPlusEnabled: plus.isEnabled,
            isPlusExclusiveEnabled,
        }),
    );

    const config = useConfig();
    const isPrive = config.isPriveDomain;
    const fs_link = config.fashion_store_link;

    const tabName = tabs?.find(t => t.id === categoryTabId)?.name;
    const customerGender = useSelector(getGender)?.toUpperCase();
    const tabGender = tabs?.find(t => t.id === categoryTabId)?.gender;

    const gender = (tabGender || customerGender || 'FEMALE') as 'MALE' | 'FEMALE' | 'UNISEX';

    const [shouldShowLoyaltyOnboardingScreen, loyaltyOnboardingScreenShown] =
        useLoyaltyOnboardingScreen(config.appdomain);
    const [dialogType, dialogUrl, markDialogAsShown] = usePlusContentDialog(true);
    const loyalty = useLoyalty();

    // Free Delivery benefit logic
    const hasFreeDeliveryBenefit = useMemo(() => {
        return loyalty?.summary?.benefits?.some(benefit => benefit.name === 'FREE_DELIVERY');
    }, [loyalty?.summary?.status]);

    const minimumOrderValue = useMemo(() => {
        const isFreeDelivery = loyalty?.summary?.benefits?.filter(benefit => {
            return benefit.name === 'FREE_DELIVERY';
        });

        if (isFreeDelivery?.length > 0) {
            return isFreeDelivery[0].minimumOrderValue;
        }
    }, [loyalty?.summary?.status]);

    const isLoyaltyMember = loyalty?.summary?.status === LoyaltyStatus.Active;
    const isLoyaltyEligible = loyalty?.summary?.status === LoyaltyStatus.Eligible;
    const noOtherModalIsShown = useMemo(() => {
        return (
            !isPlatformJourneyEnabled &&
            !isEligibleForVoucher &&
            (isMailConfirmed || !location.search)
        );
    }, [isPlatformJourneyEnabled, isEligibleForVoucher, isMailConfirmed]);

    // Show free delivery modal logic - customize this condition based on your requirements
    const shouldShowFreeDeliveryModal = useMemo(() => {
        const hasSeenModalRecently = getCookie(FREE_DELIVERY_COOKIE) === 'true';
        const showFDModal =
            noOtherModalIsShown &&
            hasFreeDeliveryBenefit &&
            ((isLoyaltyMember && !dialogType) || isLoyaltyEligible) &&
            !hasSeenModalRecently;

        return showFDModal;
    }, [
        dialogType,
        noOtherModalIsShown,
        hasFreeDeliveryBenefit,
        isLoyaltyMember,
        isLoyaltyEligible,
    ]);

    const shouldShowLoyaltyOnboardingModal = useMemo(() => {
        const hasSeenOnboardingModalRecently = getCookie(LOYALTY_OPT_IN_ONBOARDING_DIALOG_COOKIE);
        const hasSeenFreeDeliveryModalRecently = getCookie(FREE_DELIVERY_SESSION_COOKIE) === 'true';

        const showOnboardingForEligible =
            isLoyaltyEligible &&
            !!dialogType &&
            noOtherModalIsShown &&
            !shouldShowFreeDeliveryModal &&
            !hasSeenOnboardingModalRecently &&
            !hasSeenFreeDeliveryModalRecently;

        return showOnboardingForEligible;
    }, [
        noOtherModalIsShown,
        dialogType,
        hasFreeDeliveryBenefit,
        isLoyaltyMember,
        isLoyaltyEligible,
        shouldShowFreeDeliveryModal,
    ]);

    const [showFreeDeliveryDialog, setShowFreeDeliveryDialog] = useState(false);

    useEffect(() => {
        if (shouldShowFreeDeliveryModal && !showFreeDeliveryDialog) {
            setShowFreeDeliveryDialog(true);
            setCookie(FREE_DELIVERY_SESSION_COOKIE, 'true', ONE_HOUR); // session cookie
        }
    }, [shouldShowFreeDeliveryModal]);

    useEffect(() => {
        if (shouldShowLoyaltyOnboardingScreen) {
            trackViewContent('loyalty_onboarding');
        }
    }, [shouldShowLoyaltyOnboardingScreen]);

    const [showExpiredCampaignDialog, setShowExpiredCampaignDialog] = useState(
        () =>
            !!query?.status &&
            !upcomingCampaigns.some(uc => uc.campaignId === query.event) &&
            query.event !== 'showstopper',
    );

    const expiredMessage =
        query?.status === 'upcoming' || query?.status === 'open'
            ? CampaignExpiredStatus.NOT_FOUND
            : CampaignExpiredStatus.EXPIRED;

    const handleExpiredDialogClose = useCallback(() => setShowExpiredCampaignDialog(false), []);

    const { shouldRenderGRECarousel, greData, greTabData, greCarouselCampaigns, isCyberWeek } =
        useGRE();

    useEffect(() => {
        if (!window || !window.sessionStorage) {
            return;
        }
        const tabGenderValue = tabGender?.toUpperCase();
        if (tabGenderValue) {
            window.sessionStorage.setItem('selected_fashion_preference', tabGenderValue);
        } else {
            window.sessionStorage.removeItem('selected_fashion_preference');
        }
    });

    const handleCloseLoyaltyOnboardingScreen = () => {
        loyaltyOnboardingScreenShown();
        trackSelectContent('loyalty_onboarding', 'click', {
            contentId: 'close',
        });
    };

    const delayModalClose = (dialogType: PlusContentDialogType) => {
        sleep(300).then(() => {
            markDialogAsShown(dialogType);
        });
    };

    const getCustomerData = useCallback(async () => {
        await dispatch(fetchCustomer(true));
    }, []);

    const handleHashNavigation = async (postMessage: MessageEvent) => {
        if (postMessage?.data?.type === 'navigate') {
            switch (postMessage?.data?.hashbang) {
                case LATER_HASH: {
                    delayModalClose(dialogType as PlusContentDialogType);
                    break;
                }
                case PLUS_HASH: {
                    delayModalClose(dialogType as PlusContentDialogType);
                    window.open(`${fs_link}plus`, '_blank');
                    break;
                }
                case TERMS_AND_CONDITIONS_HASH: {
                    window.open(`${fs_link}zalando-terms-loyalty`, '_blank');
                    break;
                }
                case CONTINUE_SHOPPING_HASH: {
                    navigate(MYLOUNGE_ROUTE);
                    await getCustomerData();
                    delayModalClose(PlusContentDialogType.LOYALTY_OPT_IN_ONBOARDING);
                    delayModalClose(PlusContentDialogType.LOYALTY_OPT_IN_SUCCESS);
                    break;
                }
                case EXPLORE_YOUR_REWARDS_HASH: {
                    await getCustomerData();
                    delayModalClose(PlusContentDialogType.LOYALTY_OPT_IN_ONBOARDING);
                    delayModalClose(PlusContentDialogType.LOYALTY_OPT_IN_SUCCESS);
                    window.open(`${fs_link}plus`, '_blank');
                    break;
                }
                default: {
                    // Handle default case
                    break;
                }
            }
        }
    };

    const handleCloseLoyaltyModalM2 = async () => {
        await getCustomerData();
        if (loyalty.summary?.status === LoyaltyStatus.Active) {
            markDialogAsShown(PlusContentDialogType.LOYALTY_OPT_IN_ONBOARDING);
            markDialogAsShown(PlusContentDialogType.LOYALTY_OPT_IN_SUCCESS);

            trackSelectContent('loyalty_onboarding', 'click', {
                contentId: 'close',
            });
        } else {
            markDialogAsShown(dialogType as PlusContentDialogType);

            trackSelectContent('loyalty_onboarding', 'click', {
                contentId: 'close',
            });
        }
    };

    const handleCallToActionLoyaltyOnboardingScreen = () => {
        loyaltyOnboardingScreenShown();
        trackSelectContent('loyalty_onboarding', 'click', {
            contentId: 'got_it',
        });
    };

    const handleTurnOnPlusClick = () => {
        RestClient.post(`/api/phoenix/loyalty/consent`)
            .withHeaders({
                'X-Touchpoint': 'best-deals',
                [CSRF_HEADER]: getCSRFCookie(XSRF) || '',
            })
            .withPayload({ consent_status: GRANTED })
            .acceptStatusCodes([200, 204])
            .send()
            .then(() => {
                setShowFreeDeliveryDialog(false);

                setCookie(FREE_DELIVERY_COOKIE, 'true', ONE_DAY * 14); // 14 days

                dispatch(
                    showHeaderFlashMessage({
                        type: 'success',
                        message: translate(
                            'free_delivery.eligible.popup.turn_on_plus_success_description',
                            { minimumOrderValue },
                        ),
                    }),
                );
            })
            .catch(e => {
                console.error('Error in giving consent', e);
            });
    };

    const handleCloseFreeDeliveryDialog = async () => {
        setCookie(FREE_DELIVERY_COOKIE, 'true', ONE_DAY * 7); // 7 days
        setShowFreeDeliveryDialog(false);
    };

    return (
        <>
            <TranslateScope path="myLounge">
                <Helmet>
                    <title>{tabName}</title>
                </Helmet>
                <S.OuterWrapper id="main" tabIndex={-1}>
                    <AccessibleHeader tag={Tag.H1}>
                        {isPrive ? 'ZALANDO PRIVÉ' : 'ZALANDO LOUNGE'}
                    </AccessibleHeader>
                    <ExpiredCampaignDialog
                        show={showExpiredCampaignDialog}
                        onClose={handleExpiredDialogClose}
                        showStatus={expiredMessage}
                    />
                    {showCrossCampaignFilterInMobile && (
                        <CrossCampaignEntrySmallScreen categoryTabId={categoryTabId} />
                    )}
                    <HeroBanner />
                    {shouldRenderGRECarousel && (
                        <GRECarouselComponent
                            isCyberWeek={isCyberWeek}
                            greData={greData}
                            greCarouselCampaigns={greCarouselCampaigns}
                            greTabData={greTabData}
                        />
                    )}

                    <AccessibleHeader tag={Tag.H2} aria-live="assertive">
                        {tabName || ''}
                    </AccessibleHeader>
                    {flashMessages && flashMessages.length !== 0 && (
                        <FlashMessage flashMessages={flashMessages} />
                    )}

                    <Grid campaigns={openCampaigns} isPlusEnabled={plus.isEnabled} />

                    <GridPageLayout>
                        <S.Wrapper id={HIGHLIGHTS_CAROUSEL_WRAPPER}>
                            <ErrorBoundary componentName="MyLoungeHighlightsCarousel">
                                <HighlightsCarousel
                                    gender={gender}
                                    navigateToPDPInCurrentTab={null}
                                />
                            </ErrorBoundary>
                        </S.Wrapper>
                    </GridPageLayout>
                    <UpcomingSection />
                    <BottomBubble
                        customer={
                            customer as {
                                firstName: CustomerState['firstName'];
                                lastName: CustomerState['lastName'];
                                customerNumber: CustomerState['customerNumber'];
                                email: CustomerState['email'];
                            }
                        }
                        loyaltyEAVariant={false}
                    />
                </S.OuterWrapper>
            </TranslateScope>
            <TranslateScope path="plus">
                <LoyaltyOnboardingScreen
                    isShown={shouldShowLoyaltyOnboardingScreen}
                    onClose={handleCloseLoyaltyOnboardingScreen}
                    onCallToAction={handleCallToActionLoyaltyOnboardingScreen}
                />

                <PlusContentDialog
                    onMessage={handleHashNavigation}
                    onClose={handleCloseLoyaltyModalM2}
                    isShown={shouldShowLoyaltyOnboardingModal}
                    src={window.location.origin + dialogUrl}
                />
            </TranslateScope>
            <FreeDeliveryBenefitsDialog
                open={showFreeDeliveryDialog}
                onClose={handleCloseFreeDeliveryDialog}
                onTurnOnPlusClick={handleTurnOnPlusClick}
                isLoyaltyMember={isLoyaltyMember}
                minimumOrderValue={minimumOrderValue}
            />
        </>
    );
};

export default MyLoungeWrapper;
